/**
 * Branded ID types for URL-bearing identifiers.
 *
 * Branded types make the compiler refuse to accept a raw string where a
 * validated identifier is required. Cost: one explicit `asX(...)` cast at
 * each trust boundary (route param entry, clipboard parse, API response).
 * Benefit: the compiler enforces "this string has been validated", which
 * is auditor-friendly documentation and a real bug-prevention layer at
 * Zoom-scale codebase size.
 *
 * Pattern is zero-cost at runtime — the brand is a phantom field that
 * exists only for the type checker.
 *
 * Wave 5 (W5.7): RoomId, ShortId, ShortLinkAlias and their as/tryAs helpers
 * are re-exported from @oxpulse/url-contract (canonical SoT). Non-URL brands
 * (PubkeyShort, Handle, PartnerOrigin) remain declared locally — they are
 * identity/partner concerns, not URL-contract concerns.
 *
 * isValidRoomId is also re-exported here for consumers that import it via
 * '$lib/routes' (the barrel re-exports types.ts via index.ts).
 */

// Re-export the 3 URL-bearing brand types and their helpers from the canonical SoT.
// asRoomId in url-contract is shape-only; full-semantic predicate is isValidRoomId.
export {
  type RoomId,
  asRoomId,
  tryAsRoomId,
  type ShortId,
  asShortId,
  tryAsShortId,
  type ShortLinkAlias,
  asShortLinkAlias,
  tryAsShortLinkAlias,
  isValidRoomId,
} from '@oxpulse/url-contract';

declare const __brand: unique symbol;

export type PubkeyShort = string & { readonly [__brand]: 'PubkeyShort' };
export type Handle = string & { readonly [__brand]: 'Handle' };
export type PartnerOrigin = string & { readonly [__brand]: 'PartnerOrigin' };

/** Hex-encoded short pubkey, ≥6 chars (lowercase hex). */
const PUBKEY_SHORT_RE = /^[0-9a-f]{6,}$/;
export function asPubkeyShort(s: string): PubkeyShort {
  if (!PUBKEY_SHORT_RE.test(s)) throw new TypeError(`invalid PubkeyShort: ${s}`);
  return s as PubkeyShort;
}
export function tryAsPubkeyShort(s: string): PubkeyShort | null {
  return PUBKEY_SHORT_RE.test(s) ? (s as PubkeyShort) : null;
}

/** Handle: alphanumeric + underscore, ≥3 chars. Matches typical username shape. */
const HANDLE_RE = /^[A-Za-z0-9_]{3,}$/;
export function asHandle(s: string): Handle {
  if (!HANDLE_RE.test(s)) throw new TypeError(`invalid Handle: ${s}`);
  return s as Handle;
}
export function tryAsHandle(s: string): Handle | null {
  return HANDLE_RE.test(s) ? (s as Handle) : null;
}

// ShortId, asShortId, tryAsShortId — re-exported from @oxpulse/url-contract above (W5.7).

/**
 * PartnerOrigin: validated HTTPS origin with hostname (not IP literal).
 *
 * Rules enforced:
 *  - scheme MUST be `https:` (HTTP allows MITM by censorship adversary)
 *  - host MUST be a hostname (IP literals + bracketed IPv6 rejected — partner
 *    edges always run on their own domains with TLS termination)
 *  - no path / query / fragment / trailing slash (this is an *origin*,
 *    not a URL)
 *
 * Future hardening (ADR-0002 §Followups): homograph detection against a
 * known-good label list (e.g. reject `oxpu1se.chat`-style attacks). Not
 * shipped in this version — requires a curated brand-allowlist.
 */
export function asPartnerOrigin(s: string): PartnerOrigin {
  let u: URL;
  try {
    u = new URL(s);
  } catch {
    throw new TypeError(`invalid PartnerOrigin: not a URL: ${s}`);
  }
  if (u.protocol !== 'https:') {
    throw new TypeError(`invalid PartnerOrigin: scheme must be https: ${s}`);
  }
  // Reject IP literals (v4 dotted-quad and bracketed v6).
  if (/^\d+\.\d+\.\d+\.\d+$/.test(u.hostname) || u.hostname.startsWith('[')) {
    throw new TypeError(`invalid PartnerOrigin: IP literal hosts rejected: ${s}`);
  }
  if (u.pathname !== '/' && u.pathname !== '') {
    throw new TypeError(`invalid PartnerOrigin: must not contain a path: ${s}`);
  }
  if (u.search || u.hash) {
    throw new TypeError(`invalid PartnerOrigin: must not contain query or fragment: ${s}`);
  }
  // URL normalises empty path to '/'; the canonical string we emit is origin().
  // Reject any input that didn't equal its own origin (catches trailing slash).
  if (s !== u.origin) {
    throw new TypeError(`invalid PartnerOrigin: must be canonical origin, got ${s} (canonical: ${u.origin})`);
  }
  return s as PartnerOrigin;
}
export function tryAsPartnerOrigin(s: string): PartnerOrigin | null {
  try {
    return asPartnerOrigin(s);
  } catch {
    return null;
  }
}

/**
 * BuiltPath — newtype for the output of `paths.ts` builders and
 * `share.ts` helpers. RedirectAction.to accepts only BuiltPath, not
 * raw string, preventing accidental unvalidated strings from entering
 * the redirector.
 *
 * Only `asBuiltPath` should be called from inside `paths.ts` / `share.ts`.
 * External code receives BuiltPath back from a builder — never constructs one.
 *
 * @internal only call from paths.ts / share.ts builders
 */
export type BuiltPath = string & { readonly [__brand]: 'BuiltPath' };
export function asBuiltPath(s: string): BuiltPath {
  return s as BuiltPath;
}

/**
 * SanitizedRoute — newtype for the output of `sanitize.ts::sanitize`.
 * Metric emit functions accept only SanitizedRoute for the route_label
 * parameter, preventing raw room IDs or full paths from flowing into
 * analytics labels.
 *
 * Only `asSanitizedRoute` should be called from inside `sanitize.ts`.
 *
 * @internal only call from sanitize.ts
 */
export type SanitizedRoute = string & { readonly [__brand]: 'SanitizedRoute' };
export function asSanitizedRoute(s: string): SanitizedRoute {
  return s as SanitizedRoute;
}

// ShortLinkAlias, asShortLinkAlias, tryAsShortLinkAlias — re-exported from @oxpulse/url-contract above (W5.7).
